import type { KeyboardEvent, MutableRefObject, RefObject } from 'react';
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    FloatingFocusManager,
    FloatingPortal,
    autoUpdate,
    offset,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
} from '@floating-ui/react';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import {
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    Command as CommandPrimitive,
} from '../../shadcn/components/ui/command';
import type { CommandSelectItem, CommandSelectOption, CommandSelectProps } from './types';
import { isCommandSelectGroupTitle, isCommandSelectOption } from './types';
import {
    debounce,
    defaultLocalFilter,
    getSelectableOptions,
    indexOfSelectableOption,
    isEmptyAfterFilter,
    isFirstSelectableOption,
} from './utils';

interface CommandSelectContextValue {
    open: boolean;
    setOpen: (open: boolean) => void;
    value: string | number | null;
    selectedOption: CommandSelectOption | null;
    items: CommandSelectItem[];
    resolvedItems: CommandSelectItem[];
    query: string;
    setQuery: (query: string) => void;
    focusedIndex: number;
    setFocusedIndex: (index: number) => void;
    loading: boolean;
    selectOption: (option: CommandSelectOption | null) => void;
    selectByIndex: (index: number) => void;
    showCreate: boolean;
    onCreateClick: () => void;
    createActionLabel?: string | ((q: string) => string);
    searchInputRef: RefObject<HTMLInputElement>;
    listRef: RefObject<HTMLDivElement>;
    optionRefs: MutableRefObject<Map<number, HTMLDivElement>>;
    selectableOptions: CommandSelectOption[];
    treatIndex0AsNull?: boolean;
    enableNumericShortcuts?: boolean;
    numericShortcutOffset?: number;
    focusSearchOnKey?: 's' | null;
}

const CommandSelectContext = createContext<CommandSelectContextValue | null>(null);

function useCommandSelectContext() {
    const context = useContext(CommandSelectContext);
    if (!context) {
        throw new Error('CommandSelect subcomponents must be used within CommandSelect');
    }
    return context;
}

const CommandSelectComponent = forwardRef<HTMLButtonElement, CommandSelectProps>(
    (
        {
            items,
            value: valueProp,
            defaultValue = null,
            onChange,
            label,
            icon,
            badgeVariant = 'outline',
            size: sizeProp = 'md',
            className,
            fullWidth = false,
            open: openProp,
            defaultOpen = false,
            onOpenChange,
            disabled = false,
            treatIndex0AsNull = false,
            searchable = false,
            searchPlaceholder = 'Rechercher…',
            autoFocusSearchOnOpen = false,
            focusSearchOnKey = 's',
            onSearchChange,
            filterOptions,
            loadOptions,
            loadingText = '',
            emptyText = '',
            onCreate,
            createActionLabel,
            showCreateWhenNoMatch = true,
            enableNumericShortcuts = false,
            numericShortcutOffset = 1,
            ariaLabel,
            ariaLabelledBy,
            portal = true,
            placement = 'bottom-start',
            maxHeight = 300,
            resetSearchOnClose = true,
            ...rest
        },
        ref
    ) => {
        if (!label && !icon) {
            console.warn('CommandSelect: fournir au moins label ou icon.');
        }
        const { t } = useTranslation();
        if (emptyText === '') {
            emptyText = t('No results found');
        }
        if (loadingText === '') {
            loadingText = t('Loading...');
        }
        const isControlled = valueProp !== undefined;
        const [uncontrolledValue, setUncontrolledValue] = useState<string | number | null>(defaultValue);
        const value = isControlled ? valueProp! : uncontrolledValue;

        const isOpenControlled = openProp !== undefined;
        const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
        const open = isOpenControlled ? openProp : uncontrolledOpen;
        const setOpen = useCallback(
            (newOpen: boolean) => {
                if (!isOpenControlled) {
                    setUncontrolledOpen(newOpen);
                }
                onOpenChange?.(newOpen);
            },
            [isOpenControlled, onOpenChange]
        );

        const [query, setQuery] = useState('');
        const [loading, setLoading] = useState(false);
        const [resolvedItems, setResolvedItems] = useState<CommandSelectItem[]>(items);
        const [focusedIndex, setFocusedIndex] = useState(0);
        const searchInputRef = useRef<HTMLInputElement>(null);
        const listRef = useRef<HTMLDivElement>(null);
        const optionRefs = useRef<Map<number, HTMLDivElement>>(new Map());

        const { refs, floatingStyles, context } = useFloating({
            open,
            onOpenChange: setOpen,
            placement,
            strategy: 'fixed',
            middleware: [
                offset(8),
                shift(),
                size({
                    apply({ availableHeight, elements, rects }) {
                        const maxH = Math.min(maxHeight, availableHeight - 16);
                        elements.floating.style.maxHeight = `${maxH}px`;
                        elements.floating.style.width = 'auto';
                        elements.floating.style.minWidth = `${rects.reference.width}px`;
                        const contentWidth = elements.floating.scrollWidth;
                        const finalWidth = Math.max(rects.reference.width, contentWidth);
                        elements.floating.style.width = `${finalWidth}px`;
                    },
                }),
            ],
            whileElementsMounted: autoUpdate,
        });

        const click = useClick(context);
        const dismiss = useDismiss(context);
        const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

        const selectableOptions = useMemo(() => getSelectableOptions(resolvedItems), [resolvedItems]);

        const selectedOption = useMemo(() => {
            if (value === null) return null;
            return selectableOptions.find((o) => o.id === value) || null;
        }, [value, selectableOptions]);

        useEffect(() => {
            let active = true;

            const run = async () => {
                if (!searchable) {
                    setResolvedItems((prev) => (items.length === 0 ? prev : items));
                    return;
                }

                if (loadOptions) {
                    setLoading(true);
                    try {
                        const result = await loadOptions(query);
                        if (active) {
                            setResolvedItems(result);
                        }
                    } catch (error) {
                        console.error('Error loading options:', error);
                        if (active) {
                            setResolvedItems([]);
                        }
                    } finally {
                        if (active) {
                            setLoading(false);
                        }
                    }
                } else if (filterOptions) {
                    setResolvedItems(filterOptions(query, items));
                } else if (onSearchChange) {
                    setResolvedItems((prev) => (items.length === 0 ? prev : items));
                } else {
                    setResolvedItems(defaultLocalFilter(query, items));
                }
            };

            if (loadOptions) {
                const debouncedRun = debounce(run, 200);
                debouncedRun();
                return () => {
                    active = false;
                    debouncedRun.cancel();
                };
            } else {
                run();
                return () => {
                    active = false;
                };
            }
        }, [query, items, searchable, filterOptions, loadOptions, onSearchChange]);

        useEffect(() => {
            onSearchChange?.(query);
        }, [query, onSearchChange]);

        useEffect(() => {
            if (open && searchable && autoFocusSearchOnOpen && searchInputRef.current) {
                requestAnimationFrame(() => {
                    searchInputRef.current?.focus();
                });
            }
        }, [open, searchable, autoFocusSearchOnOpen]);

        useEffect(() => {
            if (!open) {
                if (resetSearchOnClose) {
                    setQuery('');
                }
                setFocusedIndex(0);
            } else if (open && selectedOption) {
                const selectedIndex = indexOfSelectableOption(selectableOptions, selectedOption);
                if (selectedIndex >= 0) {
                    setFocusedIndex(selectedIndex);
                }
            }
        }, [open, selectedOption, selectableOptions, resetSearchOnClose]);

        const selectOption = useCallback(
            (option: CommandSelectOption | null) => {
                if (!isControlled) {
                    setUncontrolledValue(option?.id ?? null);
                }
                let finalValue: string | number | null = option?.id ?? null;
                let finalOption: CommandSelectOption | null = option;
                if (treatIndex0AsNull && option) {
                    const index = indexOfSelectableOption(selectableOptions, option);
                    if (index === 0) {
                        finalValue = null;
                        finalOption = null;
                    }
                }
                onChange?.(finalValue, finalOption);
                setOpen(false);
            },
            [isControlled, treatIndex0AsNull, selectableOptions, onChange, setOpen]
        );

        const selectByIndex = useCallback(
            (index: number) => {
                const option = selectableOptions[index];
                if (option) {
                    selectOption(option);
                }
            },
            [selectableOptions, selectOption]
        );

        const showCreate = showCreateWhenNoMatch && !!onCreate && query.length > 0 && isEmptyAfterFilter(resolvedItems);

        useEffect(() => {
            if (open && focusedIndex >= 0) {
                if (focusedIndex < selectableOptions.length) {
                    const optionElement = optionRefs.current.get(focusedIndex);
                    if (optionElement && listRef.current) {
                        optionElement.scrollIntoView({
                            block: 'nearest',
                            behavior: 'smooth',
                        });
                    }
                } else if (showCreate && listRef.current) {
                    const createElement = listRef.current.querySelector('#command-select-create') as HTMLDivElement;
                    if (createElement) {
                        createElement.scrollIntoView({
                            block: 'nearest',
                            behavior: 'smooth',
                        });
                    }
                }
            }
        }, [focusedIndex, open, selectableOptions.length, showCreate]);

        const onCreateClick = useCallback(async () => {
            if (!onCreate) return;

            const created = await onCreate(query);
            if (created) {
                selectOption(created);
            }
        }, [onCreate, query, selectOption]);

        const handleKeyDown = useCallback(
            (e: KeyboardEvent) => {
                if (disabled) return;

                if (!open) {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        setOpen(true);
                        return;
                    }
                    if (focusSearchOnKey && e.key.toLowerCase() === focusSearchOnKey) {
                        e.preventDefault();
                        setOpen(true);
                        if (autoFocusSearchOnOpen && searchInputRef.current) {
                            requestAnimationFrame(() => {
                                searchInputRef.current?.focus();
                            });
                        }
                        return;
                    }
                    return;
                }

                if (e.key === 'Escape') {
                    e.preventDefault();
                    setOpen(false);
                    return;
                }

                const isSearchInputFocused = document.activeElement === searchInputRef.current;
                if (enableNumericShortcuts && /^[0-9]$/.test(e.key) && !isSearchInputFocused) {
                    e.preventDefault();
                    const n = Number(e.key);
                    const targetIdx = n - numericShortcutOffset;
                    if (targetIdx >= 0 && targetIdx < selectableOptions.length) {
                        selectByIndex(targetIdx);
                    }
                    return;
                }

                if (
                    focusSearchOnKey &&
                    e.key.toLowerCase() === focusSearchOnKey &&
                    searchInputRef.current &&
                    !isSearchInputFocused
                ) {
                    e.preventDefault();
                    searchInputRef.current.focus();
                    return;
                }

                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const maxIndex = showCreate ? selectableOptions.length : selectableOptions.length - 1;
                    setFocusedIndex((prev) => Math.min(prev + 1, maxIndex));
                    return;
                }

                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setFocusedIndex((prev) => Math.max(prev - 1, 0));
                    return;
                }

                if (e.key === 'Enter') {
                    e.preventDefault();
                    if (focusedIndex >= 0 && focusedIndex < selectableOptions.length) {
                        selectByIndex(focusedIndex);
                    } else if (showCreate && focusedIndex === selectableOptions.length) {
                        onCreateClick();
                    }
                    return;
                }
            },
            [
                disabled,
                open,
                setOpen,
                focusSearchOnKey,
                autoFocusSearchOnOpen,
                enableNumericShortcuts,
                numericShortcutOffset,
                selectableOptions,
                selectByIndex,
                focusedIndex,
                showCreate,
                onCreateClick,
            ]
        );

        const showPlaceholder =
            selectedOption === null ||
            (treatIndex0AsNull && selectedOption && isFirstSelectableOption(selectedOption, resolvedItems));

        const contextValue: CommandSelectContextValue = {
            open,
            setOpen,
            value,
            selectedOption,
            items,
            resolvedItems,
            query,
            setQuery,
            focusedIndex,
            setFocusedIndex,
            loading,
            selectOption,
            selectByIndex,
            showCreate,
            onCreateClick,
            createActionLabel,
            searchInputRef,
            listRef,
            optionRefs,
            selectableOptions,
            treatIndex0AsNull,
            enableNumericShortcuts,
            numericShortcutOffset,
            focusSearchOnKey,
        };

        const listId = useId();
        const triggerId = useId();

        return (
            <CommandSelectContext.Provider value={contextValue}>
                <div
                    className={clsx('relative', { 'w-full': fullWidth }, className)}
                    data-component-name='CommandSelect'
                >
                    <button
                        type='button'
                        {...getReferenceProps({
                            ref: (node: HTMLButtonElement | null) => {
                                refs.setReference(node);
                                if (typeof ref === 'function') {
                                    ref(node);
                                } else if (ref) {
                                    (ref as MutableRefObject<HTMLButtonElement | null>).current = node;
                                }
                            },
                        })}
                        id={triggerId}
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledBy || (label ? undefined : triggerId)}
                        aria-haspopup='listbox'
                        aria-expanded={open}
                        aria-controls={listId}
                        disabled={disabled}
                        onKeyDown={handleKeyDown}
                        className={clsx(
                            'inline-flex items-center gap-2 rounded-lg border border-solid px-3 font-medium transition-colors cursor-pointer min-w-0',
                            'focus:outline-none',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            {
                                'w-full justify-between': fullWidth,
                            },
                            {
                                'h-7 text-xs': sizeProp === 'sm',
                                'h-8 text-sm': sizeProp === 'md',
                                'h-9 text-base': sizeProp === 'lg',
                                'border-gray-300 bg-white text-gray-700 hover:bg-gray-50': badgeVariant === 'outline',
                                'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200':
                                    badgeVariant === 'soft',
                                'border-transparent bg-gray-900 text-white hover:bg-gray-800': badgeVariant === 'solid',
                            }
                        )}
                        {...rest}
                    >
                        <span className='flex min-w-0 items-center gap-2'>
                            {selectedOption?.icon ?? icon}
                            <span className='truncate'>{showPlaceholder ? label : selectedOption?.label}</span>
                        </span>
                    </button>

                    {open && (
                        <>
                            {portal ? (
                                <FloatingPortal>
                                    <FloatingFocusManager
                                        context={context}
                                        modal={false}
                                    >
                                        <div
                                            ref={refs.setFloating}
                                            style={floatingStyles}
                                            {...getFloatingProps()}
                                            className='z-tooltip rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden'
                                            id={listId}
                                            onKeyDown={handleKeyDown}
                                        >
                                            <CommandSelectContent
                                                searchable={searchable}
                                                searchPlaceholder={searchPlaceholder}
                                                loading={loading}
                                                loadingText={loadingText}
                                                emptyText={emptyText}
                                                maxHeight={maxHeight}
                                            />
                                        </div>
                                    </FloatingFocusManager>
                                </FloatingPortal>
                            ) : (
                                <FloatingFocusManager
                                    context={context}
                                    modal={false}
                                >
                                    <div
                                        ref={refs.setFloating}
                                        style={floatingStyles}
                                        {...getFloatingProps()}
                                        className='z-tooltip rounded-lg border border-gray-200 bg-white shadow-lg'
                                        id={listId}
                                        onKeyDown={handleKeyDown}
                                    >
                                        <CommandSelectContent
                                            searchable={searchable}
                                            searchPlaceholder={searchPlaceholder}
                                            loading={loading}
                                            loadingText={loadingText}
                                            emptyText={emptyText}
                                            maxHeight={maxHeight}
                                        />
                                    </div>
                                </FloatingFocusManager>
                            )}
                        </>
                    )}
                </div>
            </CommandSelectContext.Provider>
        );
    }
);

CommandSelectComponent.displayName = 'CommandSelect';

export const CommandSelect = CommandSelectComponent;

interface CommandSelectContentProps {
    searchable: boolean;
    searchPlaceholder: string;
    loading: boolean;
    loadingText: string;
    emptyText: string;
    maxHeight: number;
}

function CommandSelectContent({
    searchable,
    searchPlaceholder,
    loading,
    loadingText,
    emptyText,
    maxHeight,
}: CommandSelectContentProps) {
    const { t } = useTranslation();
    const {
        resolvedItems,
        query,
        setQuery,
        loading: contextLoading,
        selectableOptions,
        focusedIndex,
        setFocusedIndex,
        showCreate,
        onCreateClick,
        createActionLabel,
        searchInputRef,
        listRef,
        optionRefs,
        selectOption,
        selectByIndex,
        value,
    } = useCommandSelectContext();

    const hasOptions = useMemo(() => resolvedItems.some((item) => isCommandSelectOption(item)), [resolvedItems]);

    const handleValueChange = useCallback(
        (val: string) => {
            setQuery(val);
        },
        [setQuery]
    );

    const handleSearchKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const maxIndex = showCreate ? selectableOptions.length : selectableOptions.length - 1;
                const nextIndex = Math.min(focusedIndex + 1, maxIndex);
                setFocusedIndex(nextIndex);
                searchInputRef.current?.blur();
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prevIndex = Math.max(focusedIndex - 1, 0);
                setFocusedIndex(prevIndex);
                searchInputRef.current?.blur();
                return;
            }

            if (e.key === 'Enter') {
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < selectableOptions.length) {
                    selectByIndex(focusedIndex);
                } else if (showCreate && focusedIndex === selectableOptions.length) {
                    onCreateClick();
                }
                return;
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                searchInputRef.current?.blur();
                return;
            }
        },
        [
            setFocusedIndex,
            selectableOptions.length,
            searchInputRef,
            focusedIndex,
            selectByIndex,
            showCreate,
            onCreateClick,
        ]
    );

    return (
        <CommandPrimitive
            ref={listRef}
            className='max-h-full'
            shouldFilter={false}
        >
            {searchable && (
                <CommandInput
                    ref={searchInputRef}
                    value={query}
                    onValueChange={handleValueChange}
                    placeholder={searchPlaceholder}
                    onKeyDown={handleSearchKeyDown}
                    className='focus:ring-0 focus:border-gray-300'
                />
            )}
            <CommandList
                style={{ maxHeight: `${maxHeight}px` }}
                className='max-h-full overflow-y-auto'
            >
                {contextLoading || loading ? (
                    <div className='px-3 py-2 text-sm text-gray-500'>{loadingText}</div>
                ) : !hasOptions && !showCreate ? (
                    <CommandEmpty>{emptyText}</CommandEmpty>
                ) : (
                    <>
                        <CommandGroup>
                            {resolvedItems.map((item, index) => {
                                if (isCommandSelectGroupTitle(item)) {
                                    const hasOptionBefore = resolvedItems
                                        .slice(0, index)
                                        .some((x) => isCommandSelectOption(x));
                                    return (
                                        <div
                                            key={`group-${index}`}
                                            className={clsx(
                                                'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500',
                                                hasOptionBefore && 'border-t border-gray-200'
                                            )}
                                        >
                                            {item.label}
                                        </div>
                                    );
                                }

                                if (!isCommandSelectOption(item)) return null;

                                const option = item as CommandSelectOption;
                                const optionIndex = selectableOptions.findIndex((o) => o.id === option.id);
                                const isSelected = option.id === value;
                                const isFocused = optionIndex === focusedIndex;

                                return (
                                    <CommandItem
                                        key={String(option.id)}
                                        ref={(el) => {
                                            if (el && optionIndex >= 0) {
                                                optionRefs.current.set(optionIndex, el);
                                            } else if (optionIndex >= 0) {
                                                optionRefs.current.delete(optionIndex);
                                            }
                                        }}
                                        id={`command-select-option-${optionIndex}`}
                                        value={String(option.id)}
                                        onSelect={() => {
                                            selectOption(option);
                                        }}
                                        className={clsx('cursor-pointer', {
                                            'bg-gray-50': isFocused,
                                        })}
                                    >
                                        {option.icon && (
                                            <span className='shrink-0 min-w-4 mr-2 flex items-center justify-center'>
                                                {option.icon}
                                            </span>
                                        )}
                                        <span className='flex-1 truncate'>{option.label}</span>
                                        {isSelected && (
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className='h-4 w-4 text-green-500 shrink-0'
                                            />
                                        )}
                                        {option.shortcutHint && (
                                            <kbd className='rounded border border-gray-300 px-1.5 py-0.5 text-xs shrink-0'>
                                                {option.shortcutHint}
                                            </kbd>
                                        )}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>

                        {showCreate && (
                            <CommandItem
                                id='command-select-create'
                                onSelect={() => {
                                    onCreateClick();
                                }}
                                onMouseEnter={() => {
                                    setFocusedIndex(selectableOptions.length);
                                }}
                                className={clsx('cursor-pointer', {
                                    'bg-gray-50': focusedIndex === selectableOptions.length,
                                })}
                            >
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    className='size-4 text-gray-500 shrink-0 mr-1'
                                />
                                <span className='flex-1'>
                                    {createActionLabel
                                        ? typeof createActionLabel === 'function'
                                            ? createActionLabel(query)
                                            : createActionLabel
                                        : t('Create “{{query}}”', { query })}
                                </span>
                            </CommandItem>
                        )}
                    </>
                )}
            </CommandList>
        </CommandPrimitive>
    );
}
